import type { KanbanStatus } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';

import { chaveDeNome } from '@/features/servicos/shared/kanban-protocolos/kanban-historico.presenter';

/**
 * Quantas cores a paleta do quadro tem. Precisa bater com a lista `$kanban-cores` do partial
 * `shared/styles/_status-kanban.scss`: a cor em si mora no CSS, e aqui só se decide a posição
 * dentro da paleta.
 */
export const TOTAL_CORES_DO_QUADRO = 6;

/** Como uma tela se refere a uma coluna do quadro: por id, por nome, ou pelos dois. */
export interface ReferenciaDeStatus {
    id?: number | null;
    nome?: string | null;
}

/**
 * Índice de cor de uma coluna, dado pela POSIÇÃO dela na lista — nunca pelo nome nem pelo id.
 *
 * <p>Colunas são cadastro (`WEB_STATUS_FLUXO`): criar, renomear ou reordenar uma no banco não pode
 * exigir mexer em CSS, e um `id` grande não pode deixar a coluna sem cor. O resto da divisão faz a
 * paleta recomeçar: um quadro com mais colunas do que cores repete a primeira em vez de nascer com
 * uma coluna incolor.</p>
 *
 * <p><b>A posição é a do array como veio do backend</b>, e não a de uma cópia reordenada: é essa a
 * ordem em que o quadro desenha as colunas (ver `colunas()` em `kanban-protocolos.ts`). Ordenar
 * aqui faria o selo do relatório sair num tom e a coluna do quadro em outro.</p>
 *
 * <p>Devolve `null` para coluna que a lista não conhece — renomeada, removida do cadastro depois do
 * movimento, ou lista ainda não carregada. Sem índice, o selo cai nos fallbacks neutros do CSS:
 * cinza é honesto, e chutar um índice pintaria o selo com a cor de OUTRA coluna.</p>
 */
export function indiceDeCorDoStatus(status: readonly KanbanStatus[], referencia: ReferenciaDeStatus): number | null {
    const posicao = posicaoDoStatus(status, referencia);
    return posicao === null ? null : posicao % TOTAL_CORES_DO_QUADRO;
}

/**
 * Posição da coluna na lista: por `id` quando a tela tem o id, por nome quando só tem o rótulo.
 *
 * <p>O id vem primeiro porque é identidade: duas colunas podem receber nomes parecidos, e o
 * histórico do quadro guarda só o NOME — daí a segunda via existir. A comparação por nome usa a
 * mesma normalização do histórico (`chaveDeNome`), senão "Em análise" e "EM ANALISE" viram colunas
 * diferentes e uma delas fica sem cor.</p>
 */
function posicaoDoStatus(status: readonly KanbanStatus[], referencia: ReferenciaDeStatus): number | null {
    if (referencia.id !== null && referencia.id !== undefined) {
        const porId = status.findIndex((coluna) => coluna.id === referencia.id);
        if (porId >= 0) {
            return porId;
        }
    }

    const chave = chaveDeNome(referencia.nome ?? '');
    if (!chave) {
        return null;
    }

    const porNome = status.findIndex((coluna) => chaveDeNome(coluna.nome) === chave);
    return porNome < 0 ? null : porNome;
}
